import { ICharacter, NotificationType, useLorebook, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { ListEmpty, Spinner, SpinnerSize } from "@next-platform/client-common-spicy-ui";
import { Lorebook } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

import ConfirmationModal from "@/components/common/ConfirmationModal";
import useToast from "@/hooks/useToast";
import { TabNames } from "@/pages/ChatbotMy/types";
import { OutletContextType } from "@/pages/Creations/CreationLayout";
import { filterAndSortResults } from "@/utils/filterAndSortResults";
import { getEmptyStateValue } from "@/utils/getEmptyStateValue";

import LorebookCard from "./LorebookCard";

export default function Lorebooks() {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const refAllBots = useRef<Lorebook[]>([]);

  const { getLorebooksData, isLoading: isLorebookLoading, lorebooksData, onUserDeleteLorebook, onUserCloneLorebook } = useLorebook();
  const [stateLorebooks, setStateLorebooks] = useState<Lorebook[]>([]);
  const navigate = useNavigate();
  const { onlyUserId: userId } = useUserInfo();
  const [stateDeleteModalVisible, setStateDeleteModalVisible] = useState(false);
  const [stateLorebookToDelete, setStateLorebookToDelete] = useState<Lorebook | null>(null);
  const [stateIsDeleting, setStateIsDeleting] = useState(false);

  useEffect(() => {
    if (lorebooksData) {
      refAllBots.current = lorebooksData;
    }
  }, [lorebooksData]);

  useEffect(() => {
    if (userId) {
      getLorebooksData();
    } else {
      setStateLorebooks([]);
    }
    return () => setStateLorebooks([]);
  }, [userId, getLorebooksData]);

  const [search, filter, sort] = useOutletContext<OutletContextType>();

  useEffect(() => {
    if (!refAllBots.current.length) return;
    const filtered = filterAndSortResults(refAllBots.current as unknown as ICharacter[], search, sort, filter.key, filter.value);

    setStateLorebooks(filtered as unknown as Lorebook[]);
  }, [search, sort, lorebooksData, filter.key, filter.value]); // Keep lorebooksData as dependency to trigger on initial load

  const onPressDelete = (lorebook: Lorebook) => {
    setStateLorebookToDelete(lorebook);
    setStateDeleteModalVisible(true);
  };

  const onPressClone = async (lorebook: Lorebook) => {
    const { data: cloned, error } = await onUserCloneLorebook(lorebook.id);
    if (error || !cloned) {
      displayToast({ message: t("myCreation:toast.lorebookClonedFailed"), type: NotificationType.error });
      return;
    }
    displayToast({ message: t("myCreation:toast.lorebookClonedSuccess"), type: NotificationType.default });
    void getLorebooksData().catch((error) => {
      displayToast({ message: error, type: NotificationType.error });
    });
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!stateLorebookToDelete) return;

    setStateIsDeleting(true);
    const { error } = await onUserDeleteLorebook(stateLorebookToDelete.id);
    if (error) {
      displayToast({ message: t("myCreation:toast.lorebookRemovedError"), type: NotificationType.error });
      setStateIsDeleting(false);
      return;
    }
    refAllBots.current = refAllBots.current.filter((bot) => bot.id !== stateLorebookToDelete.id);
    setStateLorebooks((prevData) => prevData?.filter((bot) => bot.id !== stateLorebookToDelete.id) || []);
    displayToast({ message: t("myCreation:toast.lorebookRemoved"), type: NotificationType.default });
    setStateDeleteModalVisible(false);
    setStateLorebookToDelete(null);
    setStateIsDeleting(false);
  }, [stateLorebookToDelete, onUserDeleteLorebook, displayToast, t, setStateDeleteModalVisible, setStateLorebookToDelete, setStateIsDeleting]);

  const handleDeleteCancel = () => {
    setStateDeleteModalVisible(false);
    setStateLorebookToDelete(null);
  };

  return (
    <>
      <div className="w-full h-full flex flex-col">
        {isLorebookLoading && !stateIsDeleting ? (
          <div className="flex justify-center items-center w-full h-full">
            <Spinner size={SpinnerSize.LARGE} className="mx-auto" />
          </div>
        ) : (
          <>
            {stateLorebooks.length < 1 ? (
              <div className=" flex justify-center items-center h-full">
                <ListEmpty
                  isLoading={false}
                  {...getEmptyStateValue({ searchedQuery: search, filterBy: filter.key, navigate, activeTab: TabNames.LOREBOOKS, t })}
                />
              </div>
            ) : (
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                {(stateLorebooks || [])?.map((lorebook: Lorebook) => (
                  <LorebookCard
                    key={lorebook.id}
                    lorebook={lorebook}
                    navigate={navigate}
                    onPressDelete={onPressDelete}
                    onPressClone={() => void onPressClone(lorebook)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmationModal
        heading={t("myCreation:modal.removeLorebook.title")}
        handleClose={handleDeleteCancel}
        isLoading={stateIsDeleting}
        onUserConfirmation={handleDeleteConfirm}
        confirmationMessage={t("myCreation:modal.removeLorebook.description", { name: stateLorebookToDelete?.name })}
        isVisible={stateDeleteModalVisible}
        actionButtonColor="error"
        actionButtonText={t("common:remove")}
        discardButtonText={t("common:cancel")}
      />
    </>
  );
}
